import { Component, effect, inject, input, viewChild } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CsvModes } from 'src/app/core/csv-modes.class';
import { Asset, CurrencyBreakdown } from 'src/app/models/classes/asset.class';
import { DateFormatPipe } from 'src/app/pipes/date-format.pipe';

type AssetRow = Partial<CurrencyBreakdown> & {
  masterId: number;
  name: string;
  assetType: string;
  isLinkable: boolean;
  isParentRow: boolean;
  isSubRow: boolean;
  groupKey: number;
};
import { ExportCsvService } from 'src/app/services/export-csv.service';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { CommonModule } from '@angular/common';
import { ExpandedViewSliderComponent } from 'src/app/shared/expanded-view-slider/expanded-view-slider.component';
import { InfoIconComponent } from 'src/app/shared/info-icon/info-icon.component';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { MatIconModule } from '@angular/material/icon';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';

@Component({
    selector: 'app-assets-detail-table',
    imports: [
        CommonModule,
        ExpandedViewSliderComponent,
        InfoIconComponent,
        MatTableModule,
        MatSortModule,
        DateFormatPipe,
        CurrencyFormatPipe,
        RouterModule,
        MatIconModule,
        TourMatMenu
    ],
    templateUrl: './assets-detail-table.component.html',
    styleUrls: ['./assets-detail-table.component.scss']
})
export class AssetsDetailTableComponent {
  private exportCsvService = inject(ExportCsvService);
  private dateFormatPipe = inject(DateFormatPipe);
  activatedRoute = inject(ActivatedRoute);
  activityTracking = inject(ActivityTrackingService);

  readonly portfolioName = input<string>('');
  readonly seriesName = input<string>('');
  readonly dealName = input<string>('');
  readonly asOfDate = input<string>('');
  readonly assets = input<Asset[]>([]);
  readonly currency = input<string>('USD');
  readonly investorId = input<number | undefined>(undefined);
  readonly seriesId = input<number | undefined>(undefined);
  readonly dealId = input<number | undefined>(undefined);
  readonly reportingPeriod = input<string | undefined>(undefined);
  expandView = false;
  displayedColumns: string[] = ['name', 'assetCurrency', 'acquisitionDate', 'remainingCostBasis', 'remainingMarketValue', 'statementDate','sector', 'subSector', 'exitDate'];
  expandedDisplayedColumns: string [] = ['name', 'assetCurrency', 'acquisitionDate', 'remainingCostBasis', 'remainingMarketValue', 'statementDate','sector', 'subSector', 'geography', 'country', 'status', 'exitDate'];
  columnsToDisplay: string[] = this.displayedColumns;
  readonly sort = viewChild(MatSort);
  dataSource!: MatTableDataSource<AssetRow>;
  displayShadowOnAssetsTable = false;

  private readonly rebuildDataSource = effect(() => {
    const assets = this.assets();
    const matSort = this.sort();
    if (assets) {
      const rows = this.buildRows(assets);
      const dataSource = new MatTableDataSource<AssetRow>(rows);
      dataSource.sortData = (data, sort) => this.sortGroupedRows(data, sort);
      dataSource.sort = matSort;
      this.dataSource = dataSource;
    }
  });

  private buildRows(assets: Asset[]): AssetRow[] {
    const rows: AssetRow[] = [];
    assets.forEach((asset, index) => {
      const assetsByCurrencyBreakdowns = asset.currencyBreakdown ?? [];
      const isLinkable = asset.masterId !== 0 && asset.assetType !== 'Real Estate';
      const groupKey = asset.masterId !== 0 ? asset.masterId : -(index + 1);
      if (assetsByCurrencyBreakdowns.length <= 1) {
        const breakdown = assetsByCurrencyBreakdowns[0] ?? {} as CurrencyBreakdown;
        rows.push({
          ...breakdown,
          masterId: asset.masterId,
          name: asset.name,
          assetType: asset.assetType,
          isLinkable,
          isParentRow: false,
          isSubRow: false,
          groupKey
        });
      } else {
        rows.push({
          masterId: asset.masterId,
          name: asset.name,
          assetType: asset.assetType,
          isLinkable,
          isParentRow: true,
          isSubRow: false,
          groupKey
        });
        assetsByCurrencyBreakdowns.forEach(breakdown => {
          rows.push({
            ...breakdown,
            masterId: asset.masterId,
            name: asset.name,
            assetType: asset.assetType,
            isLinkable,
            isParentRow: false,
            isSubRow: true,
            groupKey
          });
        });
      }
    });
    return rows;
  }

  private sortGroupedRows(data: AssetRow[], sort: Sort): AssetRow[] {
    if (!sort.active || sort.direction === '') {
      return data;
    }
    const groups = new Map<number, AssetRow[]>();
    const order: number[] = [];
    data.forEach(row => {
      if (!groups.has(row.groupKey)) {
        groups.set(row.groupKey, []);
        order.push(row.groupKey);
      }
      (groups.get(row.groupKey) as AssetRow[]).push(row);
    });

    const field = sort.active === 'assetCurrency' ? 'currency' : sort.active;
    const asc = sort.direction === 'asc';

    const sortKey = (rows: AssetRow[]) => {
      const isMultiBreakdown = rows.some(r => r.isParentRow);
      if (sort.active === 'assetCurrency' && isMultiBreakdown) {
        return null;
      }
      const valueRow = rows.find(r => !r.isParentRow) ?? rows[0];
      return valueRow[field as keyof AssetRow];
    };

    order.sort((a, b) => {
      const valA = sortKey(groups.get(a) as AssetRow[]);
      const valB = sortKey(groups.get(b) as AssetRow[]);
      return this.compareValues(valA, valB, asc);
    });

    return order.reduce<AssetRow[]>((acc, key) => acc.concat(groups.get(key) as AssetRow[]), []);
  }

  private compareValues(a: unknown, b: unknown, asc: boolean): number {
    const aNullish = a === null || a === undefined || a === '';
    const bNullish = b === null || b === undefined || b === '';
    if (aNullish && bNullish) return 0;
    if (aNullish) return asc ? 1 : -1;
    if (bNullish) return asc ? -1 : 1;
    if (typeof a === 'number' && typeof b === 'number') {
      return asc ? a - b : b - a;
    }
    const result = String(a).localeCompare(String(b));
    return asc ? result : -result;
  }

  exportAssetsDataToCsv(event: Event) {
    const dealName = this.dealName();
    this.activityTracking.exportToExcelClick('assets detail table', dealName);
    (event.target as HTMLButtonElement).disabled = true;
    const csvArray = [];
    const filename = this.exportCsvService.getExportFilename(CsvModes.assets, this.reportingPeriod() ?? '', this.portfolioName(), this.seriesName(), dealName);
    const csvHeader = this.exportCsvService.getCsvHeader(CsvModes.assets);
    csvArray.push(csvHeader);
    this.assets().forEach(asset => {
      const assetsByCurrencyBreakdowns = asset.currencyBreakdown ?? [];
      if (assetsByCurrencyBreakdowns.length === 0) {
        csvArray.push({ assetName: asset.name });
        return;
      }
      assetsByCurrencyBreakdowns.forEach((breakdown, idx) => {
        const data: Record<string, unknown> = {};
        data['assetName'] = idx === 0 ? asset.name : '';
        data['currency'] = breakdown.currency;
        data['acquisitionDate'] = this.dateFormatPipe.transform(breakdown.acquisitionDate, 'M/d/yy');
        data['remainingCost'] = breakdown.remainingCostBasis;
        data['remainingMarketValue'] = breakdown.remainingMarketValue;
        data['statementDate'] = this.dateFormatPipe.transform(breakdown.mostRecentSoiDate, 'M/d/yy');
        data['sector'] = breakdown.sector;
        data['subSector'] = breakdown.subSector;
        data['geography'] = breakdown.mapRegion;
        data['country'] = breakdown.country;
        data['status'] = breakdown.status;
        data['exitDate'] = this.dateFormatPipe.transform(breakdown.exitDate, 'M/d/yy');
        csvArray.push(data);
      });
    })
    const worksheets = [{name: 'Deal Assets', csvArray: csvArray}]
    this.exportCsvService.exportToExcelFile(filename, worksheets);
    (event.target as HTMLButtonElement).disabled = false;
  }

  updateColumns(expandView: boolean) {
    this.expandView = expandView
    if (expandView) {
      this.columnsToDisplay = this.expandedDisplayedColumns;
    } else {
      this.columnsToDisplay = this.displayedColumns;
    }
    this.activityTracking.tableExpandViewToggle('assets detail table', this.dealName(), expandView)
  }

  scrollAssetsTableHandler(event: Event) {
    if ((event.target as HTMLElement).scrollLeft > 0) {
      this.displayShadowOnAssetsTable = true;
    } else {
      this.displayShadowOnAssetsTable = false;
    }
  }
}
